import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';
import { AnalyticsDb } from 'lib/cosmos/analytics/analyticsDb.js';
import { CosmosDb } from 'lib/cosmos/cosmosDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { linkedItemsHelper } from 'lib/helpers/linked-items-helper.class.js';
import { IMultiSeries, ISingleSeries } from 'lib/ui/graphs/index.js';
import { ITable } from 'lib/ui/table/index.js';
import { map } from 'rxjs';
import { TypeCoreComponent } from 'src/app/pages/type-routes/type-core.component.js';

@Component({
	selector: 'mod-view-analytics',
	templateUrl: './view-analytics.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class ViewAnalyticsComponent extends TypeCoreComponent<void> {
	private readonly responseLinkedItems = signal<CosmosDb.ICosmosItem[]>([]);

	protected readonly latestPageInits = signal<AnalyticsDb.IAnalyticsItem[] | undefined>(undefined);
	protected readonly dayVisits = signal<AnalyticsDb.IAnalyticsItem[] | undefined>(undefined);

	public readonly dayVisitSeries = computed<IMultiSeries[] | undefined>(() => {
		const dayVisits = this.dayVisits();

		if (!dayVisits) {
			return undefined;
		}

		const multiSeries: IMultiSeries[] = [];
		const botPageInitSeries: ISingleSeries[] = [];
		const nonBotPageInitSeries: ISingleSeries[] = [];
		const allPageInitSeries: ISingleSeries[] = [];

		const orderedVisits = dayVisits.sort((a, b) => {
			const aDate = this.dependencies.timeService.getLuxonDateTime(a.analyticsDate);
			const bDate = this.dependencies.timeService.getLuxonDateTime(b.analyticsDate);

			return aDate.toJSDate().valueOf() - bDate.toJSDate().valueOf();
		});

		for (const dayVisit of orderedVisits) {
			botPageInitSeries.push({
				name: this.dependencies.timeService.formatDayMonthAndYear(dayVisit.analyticsDate),
				value: dayVisit.eventCount.botCount
			});
			nonBotPageInitSeries.push({
				name: this.dependencies.timeService.formatDayMonthAndYear(dayVisit.analyticsDate),
				value: dayVisit.eventCount.nonBotCount
			});
			allPageInitSeries.push({
				name: this.dependencies.timeService.formatDayMonthAndYear(dayVisit.analyticsDate),
				value: dayVisit.eventCount.allCount
			});
		}

		multiSeries.push({
			name: 'Daily visits (bot)',
			series: botPageInitSeries
		});
		multiSeries.push({
			name: 'Daily visits (users)',
			series: nonBotPageInitSeries
		});
		multiSeries.push({
			name: 'Daily visits (all)',
			series: allPageInitSeries
		});

		return multiSeries;
	});

	public readonly latestPageInitsTable = computed<ITable<AnalyticsDb.IAnalyticsItem> | undefined>(() => {
		const latestPageInits = this.latestPageInits();

		if (!latestPageInits) {
			return undefined;
		}

		const table: ITable<AnalyticsDb.IAnalyticsItem> = {
			itemType: 'Analytics',
			search: {
				type: 'local'
			},
			noItemsText: {
				noItems: `There are no events`,
				noFilteredItems: `No events matches your filter`
			},
			items: latestPageInits,
			id: (item) => item.id,
			title: {
				title: 'Page init',
				value: (item) => item.url ?? 'no url available'
			},
			subtitle: (item) => {
				return item.timezone;
			},
			avatar: (item) => {
				if (item.browser?.isBot) {
					return {
						name: item.eventType,
						image: this.appContext()?.appSetup.botImage?.[0]?.image
					};
				}

				const user = linkedItemsHelper.getLinkedItemById<UserDb.IUserItem>(
					this.responseLinkedItems(),
					item.userId ?? ''
				);

				return {
					name: item.eventType,
					image: user?.avatar
				};
			},
			fields: [
				{
					format: 'text',
					title: `Referrer`,
					value: (item) => item.referrer,
					sortBy: (item) => item.referrer,
					showOnMobiles: true
				},
				{
					format: 'date',
					title: `Created`,
					value: (item) => this.dependencies.timeService.fromNow(item.system.updated),
					sortBy: (item) => new Date(item.system.updated),
					isDefaultSort: true,
					showOnMobiles: true
				}
			],
			mainItemClick: (item) => {
				const user = linkedItemsHelper.getLinkedItemById<UserDb.IUserItem>(
					this.responseLinkedItems(),
					item.userId ?? ''
				);

				this.dependencies.dialogService.openTextDialog({
					title: `Event details`,
					avatar: user?.avatar,
					tableItems: [
						{
							iconType: 'timezone',
							title: 'timezone',
							value: item.timezone,
							valueType: 'text'
						},
						{
							iconType: 'number',
							title: 'Events count (all)',
							value: item.eventCount.allCount,
							valueType: 'text'
						},
						{
							iconType: 'date',
							title: 'Date',
							value: `${super.formatDateTime(item.system.created)}`,
							valueType: 'text'
						},
						{
							iconType: 'relativeTime',
							title: 'From now',
							value: `${super.fromNow(item.system.created)}`,
							valueType: 'text'
						},
						{
							iconType: 'user',
							title: 'E-mail',
							value: item.userEmail,
							valueType: 'text'
						},
						{
							iconType: 'log',
							title: 'Event type',
							value: item.eventType,
							valueType: 'text'
						},
						{
							iconType: 'info',
							title: 'Referrer',
							value: item.referrer,
							valueType: 'text'
						},
						{
							iconType: 'link',
							title: 'Url',
							value: item.url,
							valueType: 'text'
						},
						{
							iconType: 'browser',
							title: 'Browser',
							value: JSON.stringify(item.browser),
							valueType: 'text'
						},
						{
							iconType: 'bot',
							title: 'Is Bot',
							value: item?.browser?.isBot ? 'Yes' : 'No',
							valueType: 'text'
						}
					]
				});
			}
		};

		return table;
	});

	constructor() {
		super();

		this.dataInit = () => {
			this.initDayVisits();
			this.initLatestPageInits();
		};
	}

	private initDayVisits(): void {
		super.subscribeToObservable(
			this.httpService
				.get<AnalyticsDb.IAnalyticsItem[]>((builder) =>
					builder.withCustomApiRoute('analytics', 'getDayVisits').build()
				)
				.pipe(
					map((response) => {
						this.dayVisits.set(response.data);
						this.responseLinkedItems.update((linkedItems) => {
							return [...linkedItems, ...(response.linkedItems ?? [])];
						});
					})
				)
		);
	}

	private initLatestPageInits(): void {
		super.subscribeToObservable(
			this.httpService
				.get<AnalyticsDb.IAnalyticsItem[]>((builder) =>
					builder.withCustomApiRoute('analytics', 'getLatestPageInits').build()
				)
				.pipe(
					map((response) => {
						this.latestPageInits.set(response.data);
						this.responseLinkedItems.update((linkedItems) => {
							return [...linkedItems, ...(response.linkedItems ?? [])];
						});
					})
				)
		);
	}
}
